
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export class InputValidator {
  private static readonly XSS_PATTERNS = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
    /<img[^>]+src[^>]*>/gi,
    /<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi
  ];

  private static readonly SQL_INJECTION_PATTERNS = [
    /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION)\b)/gi,
    /(--|\/\*|\*\/|;)/g,
    /('|"|\`)/g
  ];

  static validateEmail(email: string): ValidationResult {
    const errors: string[] = [];
    
    if (!email) {
      errors.push('Email is required');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        errors.push('Invalid email format');
      }
      
      if (email.length > 254) {
        errors.push('Email too long');
      }
    }

    return { isValid: errors.length === 0, errors };
  }

  static validateUsername(username: string): ValidationResult {
    const errors: string[] = [];
    
    if (!username) {
      errors.push('Username is required');
    } else {
      if (username.length < 3) {
        errors.push('Username must be at least 3 characters');
      }
      
      if (username.length > 50) {
        errors.push('Username too long');
      }
      
      const usernameRegex = /^[a-zA-Z0-9._-]+$/;
      if (!usernameRegex.test(username)) {
        errors.push('Username contains invalid characters');
      }
    }

    return { isValid: errors.length === 0, errors };
  }

  static validatePassword(password: string): ValidationResult {
    const errors: string[] = [];
    
    if (!password) {
      errors.push('Password is required');
    } else {
      if (password.length < 8) {
        errors.push('Password must be at least 8 characters');
      }
      
      if (password.length > 128) {
        errors.push('Password too long');
      }
      
      if (!/(?=.*[a-z])/.test(password)) {
        errors.push('Password must contain lowercase letters');
      }
      
      if (!/(?=.*[A-Z])/.test(password)) {
        errors.push('Password must contain uppercase letters');
      }
      
      if (!/(?=.*\d)/.test(password)) {
        errors.push('Password must contain numbers');
      }
      
      if (!/(?=.*[@$!%*?&])/.test(password)) {
        errors.push('Password must contain special characters');
      }
    }

    return { isValid: errors.length === 0, errors };
  }

  static sanitizeInput(input: string): string {
    if (!input) return '';
    
    return input
      .trim()
      .replace(/[<>\"'&]/g, (char) => {
        const charMap: Record<string, string> = {
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#x27;',
          '&': '&amp;'
        };
        return charMap[char] || char;
      });
  }

  static detectXSS(input: string): boolean {
    return this.XSS_PATTERNS.some(pattern => pattern.test(input));
  }

  static detectSQLInjection(input: string): boolean {
    return this.SQL_INJECTION_PATTERNS.some(pattern => pattern.test(input));
  }

  static validateTenderInput(data: any): ValidationResult {
    const errors: string[] = [];
    
    if (!data.title) {
      errors.push('Tender title is required');
    } else if (data.title.length > 200) {
      errors.push('Tender title too long');
    } else if (this.detectXSS(data.title)) {
      errors.push('Invalid characters in tender title');
    }
    
    if (data.description && data.description.length > 5000) {
      errors.push('Description too long');
    } else if (data.description && this.detectXSS(data.description)) {
      errors.push('Invalid characters in description');
    }
    
    if (data.estimatedValue && (isNaN(data.estimatedValue) || data.estimatedValue < 0)) {
      errors.push('Invalid estimated value');
    }

    return { isValid: errors.length === 0, errors };
  }

  static validateUserInput(data: any): ValidationResult {
    const errors: string[] = [];
    
    const usernameValidation = this.validateUsername(data.username);
    if (!usernameValidation.isValid) {
      errors.push(...usernameValidation.errors);
    }
    
    const emailValidation = this.validateEmail(data.email);
    if (!emailValidation.isValid) {
      errors.push(...emailValidation.errors);
    }
    
    if (data.role && !['super_admin', 'procurement_officer', 'director', 'auditor', 'supplier', 'bidder'].includes(data.role)) {
      errors.push('Invalid role specified');
    }

    return { isValid: errors.length === 0, errors };
  }
}
